import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { IUserInfo, UserInfo } from '../../../../core-module/models/user-info';
import { AccountService } from '../../../../core-module/services/account.service';
import { UserProfileService } from '../../../../core-module/services/user-profile.service';
import { AppAccessToken } from '../../../../shared-module/models/app-access-token';
import { PersonNamePipe } from '../../../../shared-module/pipes/person-name.pipe';
import { AuthService } from '../../../../shared-module/services/auth.service';
import { JwtTokenService } from '../../../../shared-module/services/jwt-token.service';
import { LocalStorageService } from '../../../../shared-module/services/local-storage.service';
import { RoutingService } from '../../../../shared-module/services/routing.service';
import { Constants } from '../../../../shared-module/utils/constants';
import { ObservableBaseComponent } from '@scl/shared/components/observable-base.component';
import { AuthStore } from '@scl/core/domains/auth/services/auth.store';

@Component({
  selector: 'app-external-profile-nav-menu',
  imports: [
    CommonModule,
    PersonNamePipe
  ],
  templateUrl: './external-profile-nav-menu.component.html',
  styleUrl: './external-profile-nav-menu.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalProfileNavMenuComponent extends ObservableBaseComponent {

  userInfo$!: Observable<IUserInfo>;
  hasAuthDataMain$!: Observable<boolean>;
  private authStore = inject(AuthStore);
  isClerkImpersonating = computed(() => this.authStore.isImpersonating());

  constructor(
    private authService: AuthService,
    private jwtTokenService: JwtTokenService,
    private routingService: RoutingService,
    private userProfileService: UserProfileService,
    private localStorageService: LocalStorageService,
    public accountService: AccountService
  ) { 
    super();
  }


  ngOnInit(): void {
    this.loadUserInfo();
    this.checkIfAuthMainData();
    //this.loadUserProfile();
  }

  returnToMainAccount() {
    if (this.authStore.isImpersonating()) {
      void this.authStore.returnFromImpersonation().then(restored => {
        if (restored) {
          this.accountService.getUserInfo()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => this.routingService.navigateAuthUser(true));
        }
      });
      return;
    }

   const authDataMain = this.localStorageService.getItem<AppAccessToken>(Constants.AuthDataMain);
       // Remove first.
       this.localStorageService.removeItem(Constants.AuthDataMain);
       // Assign token to trigger loadUserProfile and UI changes.
       this.jwtTokenService.accessToken = authDataMain;
       this.accountService.getUserInfo()
         .pipe(
           takeUntil(this.unsubscribe$),
           switchMap(usr => {
             console.log('Returning to main account:', usr);
             return of(usr);
           })
         )
         .subscribe(() => {
           this.routingService.navigateAuthUser(true);
         });
  }

  private static readonly ACTIVE_PROFILE_KEY = 'scl_active_profile';

  onLogout() {
    this.accountService.currentUser = null;
    this.userProfileService.removeUserProfile();

    if (this.authStore.isClerk()) {
      localStorage.removeItem(ExternalProfileNavMenuComponent.ACTIVE_PROFILE_KEY);
      this.authService.clearToken();
      void this.authStore.logout();
      return;
    }

    this.authService.clearTokenAndEndSession();
  }

  private checkIfAuthMainData() {
    this.hasAuthDataMain$ = this.jwtTokenService.accessToken$
      .pipe(
        map(tk => {
          const authDataMain = this.localStorageService.getItem(Constants.AuthDataMain);
          return authDataMain ? true : false;
        })
      );
  }

  private loadUserInfo() {
    // Since currentUser$ is now a computed signal, we handle it differently
    const currentUser = this.accountService.currentUser;
    if (!currentUser) {
      this.userInfo$ = this.accountService.getUserInfo()
      .pipe(
        map(usr => { 
          return usr; 
        })
      );
    } else {
      this.userInfo$ = of(currentUser);
    }
  }

}
